import React, { Suspense } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CaretDown } from "@phosphor-icons/react";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import PlanRow from "components/subscriptions/PlanRow";
import PlanScheduleNote from "components/subscriptions/PlanScheduleNote";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import { useSubscriptionContext } from "lib/contexts/SubscriptionProvider";
import { useLocale } from "lib/hooks/useLocale";
import { classNames } from "lib/utils/helpers";
import { DEFAULT_STRIP_TEXT, highlightGradient } from "lib/utils/subscriptions/planFormatters";

const ChoosePlanDialog = React.lazy(() => import("components/subscriptions/dialogs/ChoosePlanDialog"));

const STRIP_VISIBLE_HEIGHT = 24;

const ChoosePlanSection: React.FC = () => {
  const { t } = useLocale();
  const {
    state: { isEligible, mode, plans, selectedPlanId, isSwitching },
  } = useSubscriptionContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const { openPopup, activePopup, closePopup } = usePopupOrchestrator();

  const config = checkoutMetadata?.uiAttributes?.subscriptionPlanConfig;
  const card = config?.subscriptionCard;
  const borderRadius = checkoutMetadata?.uiAttributes?.paymentTileConfig?.layoutUI?.paymentCard?.borderRadius;
  const stripOverlap = borderRadius ?? 12;

  const selectedPlan =
    plans.find((plan) => plan.id === selectedPlanId) ?? plans.find((plan) => plan.isRecommended) ?? plans[0];

  const isVisible = isEligible && mode === "SUBSCRIBE" && Boolean(selectedPlan);
  const otherPlanCount = plans.length - 1;

  /* Expands and collapses on the mode switch, same motion as the shipping handles list. */
  return (
    <AnimatePresence initial={false} mode="wait">
      {isVisible && (
        <motion.div
          key="choose-plan-section"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 30, mass: 1 }}
          className="overflow-hidden">
          <div id="choose-plan-section" className="flex flex-col">
            <div className="flex w-full flex-row items-center justify-between px-2 mt-3 mb-3 text-xs">
              <h2 className="text-xs font-medium text-coal-light">{t("subscription_choose_plan")}</h2>
            </div>
            <div className={classNames("flex flex-col", isSwitching ? "opacity-60" : "")}>
              <PlanRow
                plan={selectedPlan}
                isSelected
                variant="summary"
                hasMorePlans={otherPlanCount > 0}
                isDisabled={isSwitching}
                onSelect={() => openPopup("CHOOSE_PLAN", {})}
              />

              {otherPlanCount > 0 && (
                <button
                  id="subscription-more-plans"
                  disabled={isSwitching}
                  onClick={() => openPopup("CHOOSE_PLAN", {})}
                  className="flex items-end justify-center pb-1.5 transition-[filter] hover:brightness-[0.97]"
                  style={{
                    height: STRIP_VISIBLE_HEIGHT + stripOverlap,
                    marginTop: -stripOverlap,
                    ...(borderRadius === undefined
                      ? {}
                      : { borderBottomLeftRadius: borderRadius, borderBottomRightRadius: borderRadius }),
                    background: highlightGradient(card?.highlightBackgroundColor),
                    color: card?.highlightTextColor || DEFAULT_STRIP_TEXT,
                  }}>
                  <span className="flex items-center gap-1 text-text-xs-semibold leading-[13px]">
                    {t(
                      otherPlanCount === 1 ? "subscription_more_plans_singular" : "subscription_more_plans",
                      {
                        count: otherPlanCount,
                      },
                    )}
                    <CaretDown className="h-3 w-3 shrink-0 opacity-40" weight="bold" />
                  </span>
                </button>
              )}
            </div>

            <PlanScheduleNote plan={selectedPlan} />

            <GenericDialog
              isOpen={activePopup?.key === "CHOOSE_PLAN"}
              setIsOpen={() => closePopup()}
              translateAxis="y"
              dialogOverlay={true}
              showExternalClose={true}
              customClass="overflow-visible md:!top-auto md:absolute rounded-t-2xl max-h-[81vh]"
              modalType="CHOOSE_PLAN">
              <Suspense fallback={<div className="h-40" />}>
                <ChoosePlanDialog />
              </Suspense>
            </GenericDialog>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChoosePlanSection;
